import dayjs from "dayjs";
import type { Organization } from "../../../api/v1";
import { isCommercialUsageType } from "../../libs/utils";
import { generateTorRulesSets, torRulesOption } from "../BookingForm/utils";
import type { Invoice } from "./PostDatedCheques/types";

export const paymentModeOptions = [
  { value: "cheque", label: "Cheque" },
  { value: "cash", label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "credit_card", label: "Credit Card" },
  { value: "directDebit", label: "Direct Debit" },
];

export const visibilityOptions = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "bounce", label: "Bounce" },
  { value: "settled", label: "Settled" },
  { value: "cancelled", label: "Cancel" },
  { value: "transfer_in_progress", label: "Transfer In Progress" },
];

export const bouncedReasons = [
  { value: "", label: "Select" },
  { value: "signature_mismatch", label: "Signature mismatch" },
  { value: "insufficient_funds", label: "Insufficient funds" },
  { value: "damaged_cheque", label: "Damaged cheque" },
  { value: "overwriting_on_cheque ", label: "Overwriting on cheque " },
  { value: "incorrect_amount", label: "Incorrect amount" },
];

export const removeEmptyValues = (obj) => {
  for (const key in obj) {
    if (obj[key] === "") {
      delete obj[key];
    }
  }
  return obj;
};

export const defaultFormVal = {
  status: { value: "pending", label: "Pending" },
  paymentMode: "",
  paymentRef: "",
  bankName: null,
  bankNameRef: "",
  paymentRefReplaced: "",
  reason: { value: "signature_mismatch ", label: "Signature mismatch" },
  paymentStatus: { value: "pending", label: "Pending" },
  paymentModeType: { value: "cheque", label: "Cheque" },
  depositAccount: "",
  depositBank: "",
  bouncedChequePaymentMode: { value: "", label: "" },
  bouncedChequeFee: 0,
  paymentMethodType: { label: "Full", value: 1 },
  totalBalance: 0,
  amountToPay: 0,
  dueAmount: 0,
  depositTo: { value: "", label: "" },
  subPdcRef: { value: undefined, label: undefined },
  invoicePeriod: "",
};

export const deferDefaultFormVal = {
  paymentModeType: { value: "cheque", label: "Cheque" },
  paymentStatus: { value: "pending", label: "Pending" },
  paymentRef: "",
  isApplyDefferalFee: false,
  deferralFee: 0,
  vat: 0,
  totalDeferralFee: 0,
  defeReason: "",
};

export const paymentModeRendering = (data) => {
  if (data?.params?.data?.description === "landlord_payable") {
    return paymentModeOptions?.filter((item) => item?.value !== "credit_card");
  } else {
    return paymentModeOptions;
  }
};

const isFutureDate = (current: string) => {
  const currentDate = dayjs(current);
  return currentDate.isAfter(dayjs(), "day");
};

export const setActionOptions = (data: any, organization?: Organization, canChequeCleared?: boolean) => {
  const {
    chequeDeferral,
    creditNote,
    deferred,
    deferredRequest,
    depositDate,
    propertyDetails,
    recallChequeRequestStatus,
    receiptNo,
    subPdcs,
    torType,
  } = (data?.data as Invoice) || {};

  const isRecallChequePending = recallChequeRequestStatus === "pending";
  const hasReceiptNo = Boolean(receiptNo) || (subPdcs?.length > 0 && subPdcs.some((pdc) => pdc?.receiptNo));

  const mode = data?.data?.modeOfPayment?.toLowerCase() || "";
  const chequeStatus = data?.data?.chequeStatus?.toLowerCase() || "";

  // Payment type flags
  const isCheque = mode === "cheque";
  const isCash = mode === "cash";
  const isDirectDebit = mode === "directdebit";
  const isCreditCard = ["credit_card", "credit card"].includes(mode);
  const isBankTransfer = ["bank_transfer", "bank transfer"].includes(mode);
  const deferralApproval = chequeDeferral?.approval?.toLowerCase() === "enable" || false;
  const isDeferred = deferred && deferralApproval && deferredRequest === "pending";
  const isCreditNoteRequestPending = creditNote?.status === "pending";
  const enableChequeClearance = Boolean(organization?.enableChequeClearance);

  let isDeferredStatus = chequeStatus === "deferred";
  let isCheckStatusBounced = chequeStatus === "bounced";
  let isCleared = data?.data?.isCleared;
  let isReceiptNoGenerated = data?.data?.receiptNo;
  const isCommercial = isCommercialUsageType(propertyDetails?.usagesType);

  let subPdcsPartial =
    data?.data?.subPdcs?.length > 0 &&
    data?.data?.subPdcs?.find(
      (pdc) => pdc?.paymentMode?.toLowerCase() === "cheque" && pdc?.status?.toLowerCase() === "paid"
    ) &&
    data?.data?.paymentMethodType?.toLowerCase() === "partial";

  const isDraft = chequeStatus === "draft";

  const ACTIONS = {
    edit: {
      id: "edit",
      value: "edit",
      label: "Edit",
      hidden: Boolean(isDeferred || isDeferredStatus || isCleared || isCreditNoteRequestPending),
    },
    markcleared: {
      id: "mark_cleared",
      value: "mark_cleared",
      label: "Mark as Cleared",
      hidden: Boolean(
        !isReceiptNoGenerated || isCleared || !enableChequeClearance || isCreditNoteRequestPending || !canChequeCleared
      ),
    },
    receivePayment: {
      id: "receivePayment",
      value: "confirm_payment",
      label: "Receive Payment",
      hidden: Boolean(isFutureDate(depositDate) || isDeferred || isDeferredStatus || isCreditNoteRequestPending),
    },
    markAsBounced: {
      id: "markAsBounced",
      value: "mark_as_bounced",
      label: "Mark as Bounced",
      hidden: Boolean(isDeferred || isDeferredStatus || isCleared || isCreditNoteRequestPending),
    },
    cancelPayment: {
      id: "cancelPayment",
      value: "cancelled",
      label: "Cancel Payment",
      hidden: Boolean(isDeferred || isDeferredStatus || isCreditNoteRequestPending),
    },
    replaceCheque: {
      id: "replaceCheque",
      value: "replace_cheque",
      label: "Replace Cheque",
      hidden: Boolean(isDeferred || isDeferredStatus || isCreditNoteRequestPending),
    },
    history: { id: "history", value: "history", label: "History", hidden: false },
    generateInvoiceOrTor: (label: string) => ({
      id: "generateTor",
      value: "generateTor",
      label,
      hidden: false,
    }),
    viewTorLedger: {
      id: "viewTorLedger",
      value: "viewTorLedger",
      label: "View TOR Ledger",
      hidden: false,
    },
    printInvoice: {
      id: "printInvoice",
      value: "printInvoice",
      label: "Print Invoice",
      hidden: Boolean(isCleared),
    },
    applyCreditNote: { id: "applyCreditNote", value: "applyCreditNote", label: "Apply Credit Note", hidden: false },
    approveOrRejectCreditNote: {
      id: "approveOrRejectCreditNote",
      value: "approveOrRejectCreditNote",
      label: "Approve/Reject Credit Note",
      hidden: false,
    },
    approvePendingRequest: {
      id: "approve_pending_request",
      value: "approve_pending_request",
      label: "Approve Pending Request",
      hidden: false,
    },
    rejectPendingRequest: {
      id: "reject_pending_request",
      value: "reject_pending_request",
      label: "Reject Pending Request",
      hidden: false,
    },
    deferChequeDeposit: {
      id: "defer_cheque_deposit",
      value: "defer_cheque_deposit",
      label: "Defer Cheque Deposit",
      hidden: false,
    },
    rejectTransfer: {
      id: "rejectTransfer",
      value: "rejectTransfer",
      label: "Reject Transfer",
      hidden: false,
    },
    recallCheque: {
      id: "recallCheque",
      value: "recallCheque",
      label: "Recall Cheque",
      hidden: Boolean(
        !isCheque ||
        isCleared ||
        isDeferredStatus ||
        isDeferred ||
        isCheckStatusBounced ||
        isRecallChequePending ||
        !hasReceiptNo,
      ),
    },
    approveRejectRecallCheque: {
      id: "approveRejectRecallCheque",
      value: "approveRejectRecallCheque",
      label: "Approve/Reject Recall Cheque",
      hidden: !isRecallChequePending,
    },
  };

  const { turnoverAllowedTorValues } = generateTorRulesSets(torRulesOption);
  const hasValidTorType = torType != null && turnoverAllowedTorValues.has(torType);

  const applyCreditNoteActions =
    Boolean(organization?.enableCreditNotes) && !isDeferred
      ? isCreditNoteRequestPending
        ? [ACTIONS.approveOrRejectCreditNote]
        : [ACTIONS.applyCreditNote]
      : [];

  const invoicingActions = [
    ...(isCommercial && hasValidTorType
      ? [ACTIONS.generateInvoiceOrTor(`${isDraft ? "Generate" : "Regenerate"} Invoice`), ACTIONS.viewTorLedger]
      : []),
    ACTIONS.printInvoice,
  ];

  const deferActions =
    !isCreditNoteRequestPending && isCheque
      ? [...(isDeferred ? [ACTIONS.approvePendingRequest, ACTIONS.rejectPendingRequest] : [ACTIONS.deferChequeDeposit])]
      : [];

  const OPTIONS = {
    base: [ACTIONS.edit, ...invoicingActions, ACTIONS.history],
    cancel: [
      ACTIONS.edit,
      ACTIONS.receivePayment,
      ...applyCreditNoteActions,
      ...deferActions,
      ACTIONS.cancelPayment,
      ACTIONS.recallCheque,
      ACTIONS.approveRejectRecallCheque,
      ...invoicingActions,
      ACTIONS.history,
    ],
    paidCheque: [
      ACTIONS.edit,
      ACTIONS.markAsBounced,
      ACTIONS.markcleared,
      ACTIONS.recallCheque,
      ACTIONS.approveRejectRecallCheque,
      ...invoicingActions,
      ACTIONS.history,
    ],
    paidOther: [ACTIONS.edit, ...invoicingActions, ACTIONS.history],
    bounce: [ACTIONS.replaceCheque, ACTIONS.cancelPayment, ...invoicingActions, ACTIONS.history],
    cancelled: [ACTIONS.history],
    settledCheque: [ACTIONS.edit, ACTIONS.markAsBounced, ACTIONS.recallCheque, ACTIONS.approveRejectRecallCheque, ...invoicingActions, ACTIONS.history],
    partialBounceCheque: [
      ACTIONS.edit,
      ACTIONS.receivePayment,
      ...applyCreditNoteActions,
      ACTIONS.markAsBounced,
      ...deferActions,
      ACTIONS.markcleared,
      ACTIONS.recallCheque,
      ACTIONS.approveRejectRecallCheque,
      ...invoicingActions,
      ACTIONS.history,
    ],
    transferInProgress: [
      ACTIONS.edit,
      ACTIONS.receivePayment,
      ACTIONS.rejectTransfer,
      ...invoicingActions,
      ACTIONS.history,
    ],
  };

  // Decision logic
  switch (chequeStatus) {
    case "pending":
      if (isCheque || isCash || isBankTransfer || isDirectDebit || isCreditCard) {
        if (subPdcsPartial) {
          return OPTIONS.partialBounceCheque;
        } else {
          return OPTIONS.cancel;
        }
      }
      return OPTIONS.base;

    case "transfer_in_progress":
      return OPTIONS.transferInProgress;

    case "paid":
      return isCheque ? OPTIONS.paidCheque : OPTIONS.paidOther;

    case "bounce":
      return isCheque ? OPTIONS.bounce : OPTIONS.base;

    case "cancelled":
      return OPTIONS.cancelled;

    case "settled":
      return isCheque ? OPTIONS.settledCheque : OPTIONS.paidOther;

    default:
      return OPTIONS.base;
  }
};

export const gridCol = [
  {
    headerName: "Payment Mode",
    field: "modeOfPayment",
    maxWidth: 195,
    width: 165,
    cellRendererFramework: ({ data }) => {
      return data.modeOfPayment;
    },
  },
  {
    headerName: "Status",
    field: "documentNo",
    maxWidth: 110,
    width: 110,
    cellRendererFramework: ({ data }) => `${data.status}d`,
  },
  {
    headerName: "Payment Ref",
    field: "documentNo",
    maxWidth: 195,
    width: 165,
    cellRendererFramework: ({ data }) => data.documentNo,
  },
  {
    headerName: "Bank Name",
    field: "bankName",
    maxWidth: 150,
    width: 150,
    cellRendererFramework: ({ data }) => data.bankName,
  },
];

export const formattedBankList = (configData) => {
  if (configData && configData?.bankList && Object.keys(configData?.bankList)?.length > 0) {
    const bankList = configData?.bankList || {};
    return Object?.entries(bankList as Record<string, string>).map(([key, label]) => ({
      label,
      value: label.toLowerCase().replace(/\s+/g, "_"),
    }));
  } else {
    return [];
  }
};
